'use client';

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { useIsMobile } from '@/hooks';

type StarfieldModule = typeof import('./StarfieldCanvas');

const DeferredStarfield = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mobileIntersected, setMobileIntersected] = useState(false);
  const isMobile = useIsMobile();

  // On desktop, load immediately; on mobile, wait for intersection.
  const shouldLoad = !isMobile || mobileIntersected;

  const [StarfieldCanvas, setStarfieldCanvas] = useState<ComponentType | null>(
    null,
  );

  useEffect(() => {
    const node = containerRef.current;
    // No node or desktop: nothing to observe, shouldLoad is already derived.
    if (!node || !isMobile) {
      return;
    }

    // On mobile, use Intersection Observer to defer loading
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setMobileIntersected(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px', // Start loading 100px before entering viewport
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [isMobile]);

  const loadStarfield = useCallback(async () => {
    if (StarfieldCanvas) {
      return;
    }
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) {
      return;
    }

    const starfieldModule: StarfieldModule = await import('./StarfieldCanvas');
    setStarfieldCanvas(() => starfieldModule.default);
  }, [StarfieldCanvas]);

  useEffect(() => {
    if (!shouldLoad || StarfieldCanvas) {
      return;
    }

    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const start = () => {
      if (cancelled) {
        return;
      }

      loadStarfield().catch(() => {
        /* swallow */
      });
    };

    if (typeof window === 'undefined') {
      return undefined;
    }

    const globalWindow = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (handle: number) => void;
      };

    // Use longer delays on mobile to reduce CPU impact
    // Increased delays for above-the-fold components
    const idleTimeout = isMobile ? 5000 : 1000; // Increased from 3s to 5s
    const fallbackDelay = isMobile ? 4000 : 500; // Increased from 2s to 4s

    if (globalWindow.requestIdleCallback) {
      idleId = globalWindow.requestIdleCallback(start, {
        timeout: idleTimeout,
      });
    } else {
      timeoutId = globalWindow.setTimeout(start, fallbackDelay);
    }

    return () => {
      cancelled = true;
      if (idleId !== null) {
        globalWindow.cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== null) {
        globalWindow.clearTimeout(timeoutId);
      }
    };
  }, [loadStarfield, shouldLoad, StarfieldCanvas, isMobile]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: -1 }}
    >
      {StarfieldCanvas ? <StarfieldCanvas /> : null}
    </div>
  );
};

export default memo(DeferredStarfield);
